<form [formGroup]="thresholdForm">
    <div class="wallet-balance-grid" [class.wallet-loading]="isLoadingWalletSummary">
        <!-- Available Balance -->
        <div class="wallet-balance-card" [ngClass]="availableBalanceCardClasses">
            <div class="wallet-balance-card__header">
                <div class="wallet-balance-card__title-row">
                    <div class="section-heading">Available Balance</div>

                    <i
                        *ngIf="isAvailableBalanceWarning"
                        class="fa fa-exclamation-triangle wallet-balance-card__warning-icon">
                    </i>
                </div>

                <button
                    class="wallet-balance-card__action-btn"
                    type="button"
                    [disabled]="isRequestingTopup || !canRequestTopup"
                    (click)="requestTopup()">
                    Request Top-Up
                </button>
            </div>

            <div class="wallet-balance-card__body">
                <div class="row">
                    <div class="col-md-12">
                        <span
                            class="wallet-balance-card__value"
                            [ngClass]="availableBalanceValueClasses">
                            {{ availableBalance | currencyFormat | replace: ' ' }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="wallet-balance-card__note" *ngIf="isBelowCutoffThreshold">
                * Balance is below the cut-off threshold
            </div>

            <div
                class="wallet-balance-card__note"
                *ngIf="!isBelowCutoffThreshold && isBelowWarningThreshold">
                * Balance is below the warning threshold
            </div>
        </div>

        <!-- Cashbacks Allocated -->
        <div class="wallet-balance-card">
            <div class="section-heading">Cashbacks Allocated</div>

            <div class="wallet-balance-card__body">
                <span class="wallet-balance-card__value">
                    {{ cashbacksAllocated | currencyFormat | replace: ' ' }}
                </span>
            </div>

            <div class="wallet-balance-card__note">
                * Cashback allocations made in the past 30 days
            </div>
        </div>

        <!-- Pending Cashback Allocations -->
        <div class="wallet-balance-card">
            <div class="section-heading">Pending Cashback Allocations</div>

            <div class="wallet-balance-card__body">
                <span class="wallet-balance-card__value">
                    {{ pendingCashbackAllocations | currencyFormat | replace: ' ' }}
                </span>
            </div>

            <div class="wallet-balance-card__note">
                * Cashback allocations made in the past 30 days
            </div>
        </div>

        <!-- Warning Threshold -->
        <div class="wallet-balance-card" [ngClass]="warningThresholdCardClasses">
            <div class="wallet-balance-card__header">
                <div class="section-heading">Balance Warning Threshold</div>

                <button
                    *ngIf="!isEditingWarningThreshold"
                    class="wallet-balance-card__icon-btn"
                    type="button"
                    (click)="startWarningThresholdEdit()">
                    <i class="fa fa-pencil"></i>
                </button>

                <button
                    *ngIf="isEditingWarningThreshold"
                    class="wallet-balance-card__icon-btn"
                    type="button"
                    (click)="cancelWarningThresholdEdit()">
                    <i class="fa fa-times"></i>
                </button>
            </div>

            <ng-container *ngIf="!isEditingWarningThreshold; else editWarningThreshold">
                <div class="wallet-balance-card__body">
                    <span
                        class="wallet-balance-card__value"
                        [ngClass]="warningThresholdValueClasses">
                        {{ warningThreshold | currencyFormat | replace: ' ' }}
                    </span>
                </div>
            </ng-container>

            <ng-template #editWarningThreshold>
                <div
                    class="wallet-balance-card__edit"
                    [class.wallet-balance-card__edit--disabled]="isSavingWarningThreshold">
                    <input
                        class="wallet-balance-card__input"
                        type="number"
                        formControlName="warningThreshold"
                        placeholder="Enter warning threshold" />

                    <div
                        class="wallet-balance-card__error"
                        *ngIf="
                            thresholdForm.hasError('warningBelowCutoff') &&
                            thresholdForm.get('warningThreshold')?.touched
                        ">
                        Warning threshold cannot be below cut-off threshold
                    </div>

                    <button
                        class="wallet-balance-card__save-btn"
                        type="button"
                        [disabled]="isSavingWarningThreshold"
                        (click)="saveWarningThreshold()">
                        <ng-container *ngIf="!isSavingWarningThreshold; else savingWarningTpl">
                            Save
                        </ng-container>
                    </button>
                </div>
            </ng-template>

            <ng-template #savingWarningTpl>
                <i class="fa fa-spinner fa-spin"></i> Saving...
            </ng-template>
        </div>

        <!-- Cut-off Threshold -->
        <div class="wallet-balance-card" [ngClass]="cutoffThresholdCardClasses">
            <div class="wallet-balance-card__header">
                <div class="section-heading">Balance Cut-off Threshold</div>

                <button
                    *ngIf="!isEditingCutoffThreshold"
                    class="wallet-balance-card__icon-btn"
                    type="button"
                    (click)="startCutoffThresholdEdit()">
                    <i class="fa fa-pencil"></i>
                </button>

                <button
                    *ngIf="isEditingCutoffThreshold"
                    class="wallet-balance-card__icon-btn"
                    type="button"
                    (click)="cancelCutoffThresholdEdit()">
                    <i class="fa fa-times"></i>
                </button>
            </div>

            <ng-container *ngIf="!isEditingCutoffThreshold; else editCutoffThreshold">
                <div class="wallet-balance-card__body">
                    <span
                        class="wallet-balance-card__value"
                        [ngClass]="cutoffThresholdValueClasses">
                        {{ cutoffThreshold | currencyFormat | replace: ' ' }}
                    </span>
                </div>
            </ng-container>

            <ng-template #editCutoffThreshold>
                <div
                    class="wallet-balance-card__edit"
                    [class.wallet-balance-card__edit--disabled]="isSavingCutoffThreshold">
                    <input
                        class="wallet-balance-card__input"
                        type="number"
                        formControlName="cutoffThreshold"
                        placeholder="Enter cut-off threshold" />

                    <div
                        class="wallet-balance-card__error"
                        *ngIf="
                            thresholdForm.hasError('cutoffAboveWarning') &&
                            thresholdForm.get('cutoffThreshold')?.touched
                        ">
                        Cut-off threshold cannot exceed warning threshold
                    </div>

                    <button
                        class="wallet-balance-card__save-btn"
                        type="button"
                        [disabled]="isSavingCutoffThreshold"
                        (click)="saveCutoffThreshold()">
                        <ng-container *ngIf="!isSavingCutoffThreshold; else savingCutoffTpl">
                            Save
                        </ng-container>
                    </button>
                </div>
            </ng-template>

            <ng-template #savingCutoffTpl>
                <i class="fa fa-spinner fa-spin"></i> Saving...
            </ng-template>
        </div>
    </div>
</form>
